import { type Href, type Route, useRouter, useSegments } from "expo-router";
import type { RouteSegments } from "expo-router/build/typed-routes/types";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";

type HrefString = string & Href;
type Flatten<T> = T extends (infer Item)[] ? Item : T;
type RouteSegment = Flatten<RouteSegments<Route & string[]>>;

type ExtractDynamicRouteSegments<TSegments extends RouteSegments<string[]> = RouteSegments<Route>> = {
    [Index in keyof TSegments]: TSegments[Index] extends `[${string}]` ? TSegments[Index] : never;
};
type ExtractFixedRouteSegments<TSegments extends RouteSegments<string[]> = RouteSegments<Route>> = {
    [Index in keyof TSegments]: TSegments[Index] extends `[${string}]`
        ? never
        : TSegments[Index] extends `(${string})`
          ? never
          : TSegments[Index] extends ""
            ? never
            : TSegments[Index];
};
type DynamicRouteSegment = Flatten<ExtractDynamicRouteSegments>;
type FixedRouteSegment = Flatten<ExtractFixedRouteSegments>;

// Guest routes
export const ROUTE_START: HrefString = "/start";

// Auth routes
export const ROUTE_DASHBOARD: HrefString = "/dashboard";
export const ROUTE_MORE: HrefString = "/more";
export const ROUTE_RESOURCES: HrefString = "/resources";
export const ROUTE_CALENDAR: HrefString = "/calendar";
export const ROUTE_CARE_TEAM: HrefString = "/more/care-team";

export type DismissState = {
    dismiss_to: Href;
    current: HrefString;
    setReturnTo(route: HrefString | null): void;
    handleDismiss(): void;
};

export const DismissContext = createContext<DismissState>({
    dismiss_to: ROUTE_START,
    current: ROUTE_START,
    setReturnTo: (_route) => {
        return;
    },
    handleDismiss: () => {
        return;
    },
});

export function useDismiss() {
    const value = useContext(DismissContext);
    if (!value) {
        throw new Error("useDismiss must be wrapped in a <DismissProvider />");
    }
    return value;
}

const DISMISSABLE_ROUTES: HrefString[] = [
    "/dashboard",
    "/emergency-information",
    "/health-profile",
    "/health-profile/allergies",
    "/health-profile/conditions",
    "/health-profile/emergency-instructions",
    "/health-profile/immunisations",
    "/health-profile/medications",
    "/health-profile/past-symptoms",
    "/health-profile/supplements",
    "/health-profile/surgeries",
    "/health-profile/triggers",
    "/more",
    "/more/reports",
    "/more/care-team",
    "/resources",
    "/resources/goals",
    "/resources/videos",
];

function findNearestRoute(route: HrefString, authenticated: boolean): HrefString {
    return DISMISSABLE_ROUTES.find((p) => p.startsWith(route)) ?? (authenticated ? ROUTE_DASHBOARD : ROUTE_START);
}

export function DismissProvider({ children }: { children: ReactNode }) {
    const router = useRouter();
    const [dismissing, setDismissing] = useState(false);
    const [returnTo, setReturnTo] = useState<HrefString | null>(null);

    const route_segments: RouteSegment[] = useSegments();
    const authenticated = route_segments.includes("(auth)");
    const dynamic_index = route_segments.findIndex((part: RouteSegment): part is DynamicRouteSegment =>
        /^\[.+]$/.test(part),
    );
    const is_dynamic = dynamic_index >= 0;
    const fixed_segments = route_segments.filter<FixedRouteSegment>(
        (part: RouteSegment, idx): part is FixedRouteSegment =>
            /^[^[(].*?[^\])]?$/.test(part) && (!is_dynamic || idx < dynamic_index),
    );
    const top = fixed_segments[0];
    const base_path = findNearestRoute(`/${fixed_segments.join("/")}` as HrefString, authenticated);
    const bottom = fixed_segments.pop();
    const parent_path = is_dynamic
        ? base_path
        : findNearestRoute(`/${fixed_segments.join("/")}` as HrefString, authenticated);

    const dismiss_to = useMemo<HrefString>(() => {
        if (returnTo) {
            return returnTo;
        }

        switch (top) {
            case "journal":
                return ROUTE_CALENDAR;
            case "health-profile":
                return top === bottom ? ROUTE_MORE : parent_path;
            default:
                return parent_path;
        }
    }, [top, parent_path, bottom, returnTo]);

    // biome-ignore lint/correctness/useExhaustiveDependencies(router): we only care a dismissing
    // biome-ignore lint/correctness/useExhaustiveDependencies(dismiss_to): we only care a dismissing
    // biome-ignore lint/correctness/useExhaustiveDependencies(returnTo): we only care a dismissing
    useEffect(() => {
        if (dismissing) {
            setDismissing(false);
            setReturnTo(null);

            const { canGoBack, back, canDismiss, dismiss, dismissTo } = router;

            if (canGoBack()) {
                back();
            } else if (canDismiss()) {
                dismiss();
            } else {
                dismissTo(returnTo ?? dismiss_to);
            }
        }
    }, [dismissing]);

    const handleDismiss = () => {
        setDismissing(true);
    };

    const current = `/${route_segments.join("/")}` as HrefString;

    const value = {
        dismiss_to,
        current,
        handleDismiss,
        setReturnTo,
    };

    return <DismissContext.Provider value={value}>{children}</DismissContext.Provider>;
}
